import {
  getTeam, listAllTeams, listDirectMembers, listDivisions,
} from '../api/organization';
import type { OrgPerson } from '../components/PersonDetailPopup';
import { registerSessionCache } from './session-caches';

/**
 * userId → 조직 정보(부서·팀·직책·연락처) 한 명 찾기.
 *
 * 채팅 참여자를 눌렀을 때 **조직도와 같은 팝업**을 띄우려면 조직 쪽 정보가 있어야 하는데,
 * 채팅이 아는 것은 이름·직함뿐이다. 사용자 한 명만 주는 조회 경로가 없어 조직 트리를
 * 한 번 읽어 색인을 만들고 **모듈 수준에서 캐시**한다(같은 화면에서 여러 번 눌러도 1회).
 *
 * 후속: `GET /organization/users/:id` 같은 단건 경로가 생기면 이 우회는 걷어낸다.
 */
let cache: Promise<Map<string, OrgPerson>> | null = null;

async function buildIndex(): Promise<Map<string, OrgPerson>> {
  const index = new Map<string, OrgPerson>();
  const [divisions, teams, direct] = await Promise.all([
    listDivisions(), listAllTeams(), listDirectMembers(),
  ]);

  direct.forEach((u) => {
    index.set(u.id, { ...u, divisionName: null, teamName: null, duty: null });
  });

  const details = await Promise.all(teams.map(async (t) => {
    try { return { team: t, detail: await getTeam(t.id) }; } catch { return null; }
  }));

  details.forEach((d) => {
    if (!d) return;
    const divisionName = d.team.divisionName
      ?? divisions.find((x) => x.id === d.team.divisionId)?.name
      ?? null;
    d.detail.members.forEach((m) => {
      index.set(m.user.id, {
        ...m.user,
        divisionName,
        teamName: d.team.name,
        duty: m.isLeader ? '팀장' : null,
      });
    });
  });

  return index;
}

/** 조직에서 못 찾으면 null — 부르는 쪽이 아는 것만으로 채운다. */
export async function findOrgPerson(userId: string): Promise<OrgPerson | null> {
  cache ??= buildIndex().catch((err) => { cache = null; throw err; });
  try {
    return (await cache).get(userId) ?? null;
  } catch {
    return null;
  }
}

/** 테스트 전용 — 모듈 캐시 비우기. */
export function _resetOrgPersonCacheForTests(): void { cache = null; }

// ★ 로그아웃하면 비운다 — 조직에서 **보이는 범위가 사람마다 다르다**(앞 사람의 색인을 물려주지 않는다).
registerSessionCache(() => { cache = null; });
